import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'cookie_consent';

export type CookieConsentValue = 'all' | 'essential' | null;

export function getCookieConsent(): CookieConsentValue {
  try {
    const val = localStorage.getItem(CONSENT_KEY);
    if (val === 'all' || val === 'essential') return val;
  } catch {
    // localStorage unavailable (private mode, etc.)
  }
  return null;
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show banner only if no decision has been made yet
    if (getCookieConsent() === null) {
      setVisible(true);
    }
  }, []);

  function handleAccept() {
    try {
      localStorage.setItem(CONSENT_KEY, 'all');
    } catch { /* ignore */ }
    setVisible(false);
    // Reload so posthog.ts can re-check consent and initialise
    window.location.reload();
  }

  function handleEssential() {
    try {
      localStorage.setItem(CONSENT_KEY, 'essential');
    } catch { /* ignore */ }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-50"
      style={{
        background: 'color-mix(in srgb, var(--bg) 96%, transparent)',
        backdropFilter: 'blur(12px)',
        borderTop: '1px solid color-mix(in srgb, var(--green2) 18%, transparent)',
      }}
    >
      <div className="max-w-5xl mx-auto px-4 py-2.5 sm:px-6 sm:py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-[12px] text-[var(--text2)] leading-snug flex-1">
          Analytics (PostHog) + error tracking (Sentry). Your forest data stays in the EU (Supabase Frankfurt).{' '}
          <Link
            to="/privacy"
            className="text-[var(--green)] underline underline-offset-2 hover:brightness-125 transition"
          >
            Privacy Policy
          </Link>
        </p>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleEssential}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium border border-[var(--border)] text-[var(--text2)] hover:bg-[var(--bg3)] transition-colors"
          >
            Essential only
          </button>
          <button
            onClick={handleAccept}
            className="rounded-md px-3 py-1.5 text-[12px] font-semibold bg-[var(--green)] text-[var(--bg)] hover:brightness-110 transition"
          >
            Accept analytics
          </button>
        </div>
      </div>
    </div>
  );
}
